/* ===========================================================================
   Cover — diepte en de band onderaan.

   De diepte komt van de muis: foto, coverlijnen en product schuiven elk een
   andere afstand mee. Onderaan hangt dezelfde marquee-band als op de
   homepage; die wordt door initLint uit thema.js aan de scroll gekoppeld.
   =========================================================================== */

/* --------------------------------------------------------------------------
   Diepte
   Drie lagen die verschillend ver meeschuiven met de muis: de foto het minst,
   de coverlijnen een beetje, het product het meest — plus een kanteling in de
   ruimte. Dat verschil in snelheid is wat het diepte geeft; één laag die
   meebeweegt voelt alleen maar los.

   De waarden lopen na (lerp), anders plakt alles direct aan de cursor en
   wordt het nerveus.
   -------------------------------------------------------------------------- */

/* In het Shopify-thema zet window.THEMA_ASSETS prototype-paden om naar
   CDN-URL's; zonder die mapping (het prototype) blijft het pad zoals het is. */
const beeldPad = (pad) => (window.THEMA_ASSETS && window.THEMA_ASSETS[pad]) || pad;

function initCoverDiepte(sectie) {
  if (rustigeVoorkeur.matches) return;

  const foto = sectie.querySelector(".cover__achtergrond");
  const olla = sectie.querySelector("[data-cover-olla]");
  const kolommen = [...sectie.querySelectorAll(".cover__kolom")];
  if (!foto || !olla) return;

  const doel = { x: 0, y: 0 };
  const nu = { x: 0, y: 0 };
  let loopt = false;

  const teken = () => {
    // 0,08 = hoe snel hij de cursor inhaalt; lager voelt zwaarder
    nu.x += (doel.x - nu.x) * 0.08;
    nu.y += (doel.y - nu.y) * 0.08;

    foto.style.transform =
      `scale(1.06) translate3d(${nu.x * -18}px, ${nu.y * -12}px, 0)`;

    olla.style.transform =
      `translate3d(${nu.x * 54}px, ${nu.y * 34}px, 0) ` +
      `rotateY(${nu.x * 14}deg) rotateX(${nu.y * -10}deg)`;

    kolommen.forEach((kolom, i) => {
      // De twee kolommen wijken een andere kant op, als de zijkanten van een
      // doos die je kantelt.
      const kant = i === 0 ? 1 : -1;
      kolom.style.transform = `translate3d(${nu.x * 10 * kant}px, ${nu.y * 6}px, 0)`;
    });

    if (Math.abs(doel.x - nu.x) > 0.0005 || Math.abs(doel.y - nu.y) > 0.0005) {
      requestAnimationFrame(teken);
    } else {
      loopt = false;
    }
  };

  const aanzetten = () => {
    if (loopt) return;
    loopt = true;
    requestAnimationFrame(teken);
  };

  sectie.addEventListener("pointermove", (e) => {
    const r = sectie.getBoundingClientRect();
    // -0,5 links/boven, +0,5 rechts/onder
    doel.x = (e.clientX - r.left) / r.width - 0.5;
    doel.y = (e.clientY - r.top) / r.height - 0.5;
    aanzetten();
  });

  // Cursor weg: alles zakt terug naar het midden
  sectie.addEventListener("pointerleave", () => {
    doel.x = 0;
    doel.y = 0;
    aanzetten();
  });
}

/* --------------------------------------------------------------------------
   Kaartslider (rechts op de cover)
   Eén kaart die door de hoogtepunten bladert: artikelen en producten door
   elkaar, net als in de inhoudsopgave. Hij bladert vanzelf door en pauzeert
   zodra je muis erboven hangt; de bolletjes springen direct.

   In Shopify worden dit blocks (artikel/product); een product levert prijs
   en variant, een artikel de leestijd.
   -------------------------------------------------------------------------- */

const COVERSLIDES = [
  {
    id: 81001,
    kicker: "Op de cover",
    titel: "Keramische olla",
    variant: "Groen glazuur",
    prijs: "€ 24,95",
    prijsCenten: 2495,
    knop: "Bestel nu",
    href: "#product",
    beeld: "img/interieur.jpg",
  },
  {
    kicker: "Uit de werkplaats",
    titel: "Vijftig ollas, één oven",
    leestijd: "7 minuten",
    knop: "Lees het stuk",
    href: "#spread",
    beeld: "img/interieur.jpg",
  },
  {
    id: 81002,
    kicker: "Nieuw binnen",
    titel: "Linnen kussenhoes",
    variant: "Linnen, zand",
    prijs: "€ 34,95",
    prijsCenten: 3495,
    knop: "Bestel nu",
    href: "#product",
    beeld: "img/interieur.jpg",
  },
];

/* --------------------------------------------------------------------------
   Inkt-proef
   De bolletjes van de slider liggen soms op een foto en soms op papier; in
   het laatste geval vielen witte stippen op wit weg. Deze proef kijkt wat er
   echt achter een element ligt — de pixels van de foto als daar een foto
   hangt, anders de achtergrondkleur van het vlak eronder — en zet
   data-tint="licht" of "donker". De CSS kiest daar het kleurenpaar bij.

   De beelden komen van hetzelfde domein (de CDN van de winkel), dus het doek
   raakt niet besmet; lukt uitlezen toch niet, dan slaat hij dat punt over.
   -------------------------------------------------------------------------- */

const inktDoek = document.createElement("canvas");
inktDoek.width = 8;
inktDoek.height = 8;

function helderheidVanBeeld(beeld, x, y) {
  const vak = beeld.getBoundingClientRect();
  const bronB = beeld.naturalWidth;
  const bronH = beeld.naturalHeight;
  if (!vak.width || !vak.height || !bronB || !bronH) return null;

  // De foto's staan op object-fit: cover, dus een deel van de bron valt
  // buiten beeld. Reken het schermpunt terug naar de bronpixel.
  const schaal = Math.max(vak.width / bronB, vak.height / bronH);
  const zichtB = vak.width / schaal;
  const zichtH = vak.height / schaal;
  const bx = (bronB - zichtB) / 2 + ((x - vak.left) / vak.width) * zichtB;
  const by = (bronH - zichtH) / 2 + ((y - vak.top) / vak.height) * zichtH;
  const blok = Math.max(8, Math.round(zichtB * 0.1));

  try {
    const doek = inktDoek.getContext("2d", { willReadFrequently: true });
    doek.drawImage(
      beeld,
      Math.max(0, bx - blok / 2),
      Math.max(0, by - blok / 2),
      blok,
      blok,
      0,
      0,
      8,
      8
    );
    const d = doek.getImageData(0, 0, 8, 8).data;
    let som = 0;
    for (let i = 0; i < d.length; i += 4) {
      som += (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) / 255;
    }
    return som / (d.length / 4);
  } catch {
    return null;
  }
}

function inktProef(doel) {
  const vak = doel.getBoundingClientRect();
  // Buiten beeld levert elementsFromPoint niets op; dan laten we de vorige
  // uitkomst staan.
  if (!vak.width || vak.bottom < 0 || vak.top > window.innerHeight) return;

  const punten = [
    [vak.left + vak.width * 0.15, vak.top + vak.height / 2],
    [vak.left + vak.width * 0.5, vak.top + vak.height / 2],
    [vak.left + vak.width * 0.85, vak.top + vak.height / 2],
  ];

  let som = 0;
  let tel = 0;

  punten.forEach(([x, y]) => {
    for (const el of document.elementsFromPoint(x, y)) {
      if (el === doel || doel.contains(el)) continue;

      if (el.tagName === "IMG") {
        const h = helderheidVanBeeld(el, x, y);
        if (h === null) continue;
        som += h;
        tel += 1;
        return;
      }

      const m = (getComputedStyle(el).backgroundColor || "").match(
        /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+))?/
      );
      if (!m) continue;
      if (m[4] !== undefined && parseFloat(m[4]) === 0) continue;
      som += (0.299 * +m[1] + 0.587 * +m[2] + 0.114 * +m[3]) / 255;
      tel += 1;
      return;
    }
  });

  if (!tel) return;
  doel.dataset.tint = som / tel > 0.55 ? "licht" : "donker";
}

function initCoverSlider(sectie) {
  const houder = sectie.querySelector("[data-coverslider]");
  if (!houder) return;

  const venster = houder.querySelector(".coverslider__venster");
  const kaarten = [...houder.querySelectorAll(".coverslider__kaart")];
  const bolletjes = houder.querySelector("[data-bolletjes]");
  if (kaarten.length < 2 || !bolletjes || !venster) return;

  // Een herinit op dezelfde DOM (editor) mag geen tweede slider opbouwen
  if (houder.dataset.sliderInit) return;
  houder.dataset.sliderInit = "1";

  // De twee wisselkaarten uit de opmaak zijn het sjabloon: de carrousel
  // kloont er eentje per dia uit, en bij het terugschakelen naar de stapel
  // zetten we ze hier weer schoon terug.
  if (!houder._sjabloon) {
    houder._sjabloon = kaarten.map((k) => k.cloneNode(true));
  }

  // Zelfde breekpunt als de gestapelde cover in cover.css: smal krijgt geen
  // kruisvervaging maar een carrousel waarin je de volgende kaart half ziet.
  const smalMedia = window.matchMedia(
    "(max-width: 899px), (min-width: 900px) and (max-width: 1180px) and (orientation: portrait)"
  );

  // In het thema levert elk dia-blok zijn eigen JSON (echte producten en
  // artikelen); zonder die bron draaien de demo-dia's. De ankers (de
  // blok-elementen zelf) krijgen hier hun index, zodat de thema-editor
  // per dia weet welk blok erbij hoort.
  let dias = COVERSLIDES;
  const bronnen = [...houder.querySelectorAll("[data-dia-json]")];
  if (bronnen.length >= 2) {
    try {
      dias = bronnen.map((el) => JSON.parse(el.textContent));
    } catch {
      dias = COVERSLIDES;
    }
  }
  houder.querySelectorAll("[data-dia-anker]").forEach((anker, index) => {
    anker.dataset.diaIndex = String(index);
  });

  const DUUR = 6000;
  // De timerbalk in de actieve bol loopt precies zo lang als de interval
  bolletjes.style.setProperty("--slider-duur", `${DUUR}ms`);

  // Twee kaarten wisselen elkaar af: "voor" is zichtbaar, "achter" staat
  // geparkeerd onder het venster en schuift eroverheen bij de wissel.
  let voor = kaarten[0];
  let achter = kaarten[1];
  let i = 0;
  let bezig = false;
  let loop = null;

  const vul = (kaart, dia) => {
    const veld = (naam) => kaart.querySelector(`[data-veld="${naam}"]`);

    veld("beeld").src = beeldPad(dia.beeld);
    veld("beeld").alt = dia.titel;
    veld("kicker").textContent = dia.kicker;
    veld("titel").textContent = dia.titel;

    // Prijs (pil) of leestijd (kale regel): een prijs betekent te koop
    const sub = veld("sub");
    sub.textContent = dia.prijs ?? dia.leestijd;
    sub.classList.toggle("coverslider__sub--prijs", Boolean(dia.prijs));

    veld("knoptekst").textContent = dia.knop;
    veld("link").href = dia.href;

    // De knop moet weten welke dia er nu op de kaart staat
    kaart.dataset.diaIndex = dias.indexOf(dia);
  };

  // De ondergrond achter de bolletjes verandert per dia (en bij scrollen),
  // dus proeven we opnieuw: meteen, en nog eens na de overgang.
  const proefBolletjes = () => {
    inktProef(bolletjes);
    setTimeout(() => inktProef(bolletjes), 520);
  };

  let proefRust = null;
  window.addEventListener(
    "scroll",
    () => {
      if (!houder.isConnected) return;
      clearTimeout(proefRust);
      proefRust = setTimeout(() => inktProef(bolletjes), 150);
    },
    { passive: true }
  );

  const zetBols = (index) => {
    proefBolletjes();
    [...bolletjes.children].forEach((bol, b) => {
      bol.classList.toggle("is-actief", b === index);
      bol.setAttribute("aria-current", String(b === index));
    });

    // Blok-ankers van de thema-editor wisselen mee: alleen het anker van de
    // zichtbare dia staat aan (in het prototype bestaan ze niet — no-op).
    houder.querySelectorAll("[data-dia-anker]").forEach((anker) => {
      anker.hidden = Number(anker.dataset.diaIndex) !== index;
    });
  };

  // Laat de timerbalk opnieuw van nul lopen (bijvoorbeeld na een pauze)
  const herstartBalk = () => {
    const actief = bolletjes.querySelector(".is-actief");
    if (!actief) return;
    actief.style.animation = "none";
    actief.getBoundingClientRect();
    actief.style.animation = "";
  };

  const wissel = (naar) => {
    if (bezig || naar === i) return;
    bezig = true;
    i = naar;

    vul(achter, dias[naar]);
    zetBols(naar);

    // De bovenste kaart krimpt naar achteren de stapel in terwijl de kaart
    // erachter naar voren zoomt; de randjes veren mee (is-wisselt op de
    // houder). De reflow ertussen is nodig, anders slaat de overgang over.
    achter.classList.add("is-schuift");
    voor.classList.add("is-schuift");
    achter.getBoundingClientRect();
    houder.classList.add("is-wisselt");
    voor.classList.add("is-weg");
    achter.classList.add("is-boven");

    setTimeout(() => {
      // De gelande kaart springt zonder overgang terug in de parkeerstand;
      // de randjes veren los van de klasse terug omhoog.
      voor.classList.remove("is-schuift", "is-weg", "is-boven");
      achter.classList.remove("is-schuift");
      houder.classList.remove("is-wisselt");
      [voor, achter] = [achter, voor];
      bezig = false;
    }, 480);
  };

  // Waar een bolletje heen stuurt, hangt af van de modus; de knoppen zelf
  // bouwen we maar één keer.
  let gaNaar = (index) => {
    wissel(index);
    herstart();
  };

  // "Bestel nu" op een product-dia legt hem echt in de wagen (en opent de
  // lade); een artikel-dia navigeert gewoon naar het stuk.
  const koppelKoop = (lijst) => {
    lijst.forEach((kaart) => {
      kaart.querySelector('[data-veld="link"]').addEventListener("click", (e) => {
        const dia = dias[+kaart.dataset.diaIndex || 0];
        if (!dia?.prijsCenten) return;

        e.preventDefault();
        // Niet door laten borrelen naar de pagina-laag: die sluit de lade
        // zodra hij een klik ziet terwijl de lade open staat.
        e.stopPropagation();
        wagen.voegToe({
          id: dia.id ?? dia.variantId,
          variantId: dia.variantId,
          naam: dia.titel,
          variant: dia.variant ?? "",
          prijs: dia.prijsCenten,
          beeld: dia.kaartBeeld || beeldPad("img/olla-groen.png"),
          aantal: 1,
        });
        wagen.open(e.currentTarget);
      });
    });
  };

  // Van modus wisselen (draaien, of de weergaveknop in de editor) bouwt de
  // slider opnieuw op; de luisteraar geldt één keer, de nieuwe opbouw zet
  // zijn eigen luisteraar.
  const bouwOpnieuw = () => {
    clearInterval(loop);
    houder.dataset.sliderInit = "";
    delete houder.dataset.modus;
    venster.replaceChildren(...houder._sjabloon.map((k) => k.cloneNode(true)));
    houder.classList.remove("is-wisselt");
    initCoverSlider(sectie);
  };
  smalMedia.addEventListener("change", bouwOpnieuw, { once: true });

  // Bolletjes één keer opbouwen (vanaf schoon, tegen verdubbeling)
  bolletjes.replaceChildren();
  dias.forEach((dia, index) => {
    const bol = document.createElement("button");
    bol.type = "button";
    bol.className = "coverslider__bol";
    bol.setAttribute("aria-label", `Toon ${dia.titel}`);
    bol.addEventListener("click", () => gaNaar(index));
    bolletjes.append(bol);
  });

  /* --- Carrousel (smal scherm) --------------------------------------------
     Alle dia's staan naast elkaar in een schuifspoor met scroll-snap: je
     veegt ze langs en ziet de volgende kaart half meelopen. Hij bladert
     vanzelf door tot je zelf veegt; daarna laat hij je met rust. */
  if (smalMedia.matches) {
    houder.dataset.modus = "carrousel";

    const spoor = dias.map((dia) => {
      const kaart = houder._sjabloon[0].cloneNode(true);
      kaart.className = "coverslider__kaart";
      vul(kaart, dia);
      return kaart;
    });
    venster.replaceChildren(...spoor);
    koppelKoop(spoor);

    let actief = 0;
    let handmatig = false;

    // De stap tussen twee kaarten (breedte + tussenruimte) meten we uit de
    // opmaak, zodat de CSS de maten mag bepalen.
    const stap = () =>
      spoor.length > 1 ? spoor[1].offsetLeft - spoor[0].offsetLeft : 1;

    const naarDia = (index, zacht = true) => {
      if (!spoor[index]) return;
      // Meteen bijwerken in plaats van wachten op het scroll-event: dat
      // laatste is de terugweg voor het vegen zelf.
      actief = index;
      zetBols(index);
      venster.scrollTo({
        left: index * stap(),
        behavior: zacht && !rustigeVoorkeur.matches ? "smooth" : "auto",
      });
    };

    gaNaar = (index) => {
      stopCarrousel();
      naarDia(index);
    };

    // Welke kaart in beeld staat volgt uit de scrollpositie; even wachten
    // tot het vegen tot rust komt, anders knipperen de bolletjes mee.
    let rust = null;
    venster.addEventListener(
      "scroll",
      () => {
        clearTimeout(rust);
        rust = setTimeout(() => {
          const index = Math.max(
            0,
            Math.min(spoor.length - 1, Math.round(venster.scrollLeft / stap()))
          );
          if (index === actief) return;
          actief = index;
          zetBols(index);
        }, 90);
      },
      { passive: true }
    );

    function stopCarrousel() {
      clearInterval(loop);
      if (handmatig) return;
      handmatig = true;
      // Zonder doorbladeren heeft de timerbalk in het bolletje geen
      // betekenis meer; hij wordt een gewone stip.
      bolletjes.dataset.timer = "uit";
    }

    // Eén veeg en hij houdt op met zelf doorbladeren
    venster.addEventListener("pointerdown", stopCarrousel, { passive: true });
    venster.addEventListener("touchstart", stopCarrousel, { passive: true });

    const startCarrousel = () => {
      if (rustigeVoorkeur.matches || handmatig) return;
      clearInterval(loop);
      loop = setInterval(() => {
        if (!houder.isConnected) return clearInterval(loop);
        naarDia((actief + 1) % dias.length);
      }, DUUR);
      herstartBalk();
    };

    // Voor de thema-editor (editor.js)
    houder._toonDia = (index) => naarDia(index);
    houder._pauzeer = () => clearInterval(loop);
    houder._hervat = startCarrousel;

    naarDia(0, false);
    startCarrousel();
    return;
  }

  // Vanzelf doorbladeren. Bij hover pauzeert de interval; de timerbalk
  // pauzeert via CSS mee. Bij het hervatten beginnen ze allebei opnieuw,
  // zodat balk en wissel synchroon blijven.
  const start = () => {
    if (rustigeVoorkeur.matches) return;
    loop = setInterval(() => {
      // Sectie door de editor vervangen: stoppen in plaats van doorbladeren
      if (!houder.isConnected) return stop();
      wissel((i + 1) % dias.length);
    }, DUUR);
    herstartBalk();
  };

  const stop = () => clearInterval(loop);

  const herstart = () => {
    stop();
    start();
  };

  houder.addEventListener("pointerenter", stop);
  houder.addEventListener("pointerleave", start);

  koppelKoop(kaarten);

  // Voor de thema-editor (editor.js): bij blok-selectie deze dia tonen en
  // het doorbladeren pauzeren zolang het blok geselecteerd is.
  houder._toonDia = (index) => wissel(index);
  houder._pauzeer = stop;
  houder._hervat = herstart;

  vul(voor, dias[0]);
  voor.classList.add("is-boven");
  zetBols(0);
  start();
}

/* --------------------------------------------------------------------------
   In deze editie
   Artikelen en producten in één genummerde lijst. Het enige verschil in de
   opmaak is wat er rechts staat: een leestijd of een prijs. Dat is precies
   het punt van dit concept — beide horen bij de editie.

   In Shopify worden dit blocks; een block van het type "product" levert de
   prijs, een block van het type "artikel" de leestijd.
   -------------------------------------------------------------------------- */

const EDITIE = [
  {
    rubriek: "Uit de werkplaats",
    titel: "Vijftig ollas, één oven",
    leestijd: "7 minuten",
    beeld: beeldPad("img/olla-groen.png"),
    href: "#artikel",
  },
  {
    rubriek: "Op de cover",
    titel: "Keramische olla, groen glazuur",
    prijs: "€ 24,95",
    beeld: beeldPad("img/olla-groen.png"),
    href: "#product",
  },
  {
    rubriek: "Planten",
    titel: "Zes planten die vergeten willen worden",
    leestijd: "4 minuten",
    beeld: beeldPad("img/olla-groen.png"),
    href: "#artikel",
  },
  {
    rubriek: "Nieuw binnen",
    titel: "Linnen kussenhoes, zand",
    prijs: "€ 34,95",
    beeld: beeldPad("img/olla-groen.png"),
    href: "#product",
  },
  {
    rubriek: "Makers",
    titel: "Bij Anouk in de studio, tussen de ovens",
    leestijd: "5 minuten",
    beeld: beeldPad("img/olla-groen.png"),
    href: "#artikel",
  },
  {
    rubriek: "Thuis",
    titel: "Waarom je vensterbank te warm is",
    leestijd: "3 minuten",
    beeld: beeldPad("img/olla-groen.png"),
    href: "#artikel",
  },
  {
    rubriek: "Klein",
    titel: "Schaal, handgedraaid",
    prijs: "€ 22,50",
    beeld: beeldPad("img/olla-groen.png"),
    href: "#product",
  },
];

function initInhoud(wortel = document) {
  const lijst = wortel.querySelector("[data-inhoud]");
  const sjabloon = lijst?.querySelector("[data-inhoud-sjabloon]");
  if (!lijst || !sjabloon) return;

  lijst.querySelectorAll(".inhoudsregel").forEach((r) => r.remove());

  EDITIE.forEach((item, i) => {
    const regel = sjabloon.content.firstElementChild.cloneNode(true);
    const veld = (naam) => regel.querySelector(`[data-veld="${naam}"]`);

    veld("nummer").textContent = String(i + 1).padStart(2, "0");
    veld("rubriek").textContent = item.rubriek;
    veld("titel").textContent = item.titel;

    // Prijs óf leestijd; nooit allebei
    veld("prijs").hidden = !item.prijs;
    veld("leestijd").hidden = Boolean(item.prijs);
    if (item.prijs) veld("prijs").textContent = item.prijs;
    else veld("leestijd").textContent = item.leestijd;

    veld("mini").src = beeldPad(item.beeld);
    veld("mini").alt = "";

    const link = veld("link");
    link.href = item.href;
    link.setAttribute(
      "aria-label",
      item.prijs ? `${item.titel} — ${item.prijs}` : `${item.titel} — ${item.leestijd}`
    );

    lijst.append(regel);
  });
}

/* --------------------------------------------------------------------------
   Eerdere edities
   Drie ruggen; de bovenste is de editie waar je nu op staat en krijgt een
   markering in plaats van een link.
   -------------------------------------------------------------------------- */

const EDITIES = [
  {
    nummer: "04",
    maand: "Augustus",
    titel: "De koele zomer",
    beeld: beeldPad("img/olla-groen.png"),
    nu: true,
  },
  {
    nummer: "03",
    maand: "Juni",
    titel: "Alles wat langer meegaat",
    beeld: beeldPad("img/olla-groen.png"),
  },
  {
    nummer: "02",
    maand: "Mei",
    titel: "Uit de eerste stook",
    beeld: beeldPad("img/olla-groen.png"),
  },
];

function initEdities(wortel = document) {
  const rij = wortel.querySelector("[data-edities]");
  const sjabloon = rij?.querySelector("[data-editie-sjabloon]");
  if (!rij || !sjabloon) return;

  rij.querySelectorAll(".editie").forEach((e) => e.remove());

  EDITIES.forEach((editie) => {
    const kaart = sjabloon.content.firstElementChild.cloneNode(true);
    const veld = (naam) => kaart.querySelector(`[data-veld="${naam}"]`);

    veld("beeld").src = beeldPad(editie.beeld);
    veld("beeld").alt = "";
    veld("nummer").textContent = editie.nummer;
    veld("maand").textContent = editie.maand;
    veld("titel").textContent = editie.titel;

    const link = veld("link");

    if (editie.nu) {
      kaart.classList.add("editie--nu");
      // De editie die je leest is geen link
      link.remove();
    } else {
      veld("nu").remove();
      link.setAttribute("aria-label", `Editie ${editie.nummer}: ${editie.titel}`);
    }

    rij.append(kaart);
  });
}

function initCover(wortel = document) {
  const sectie = wortel.querySelector(".cover") ?? document.querySelector(".cover");
  if (!sectie) return;

  // Zet is-geladen op de body; daar hangen alle binnenkomst-animaties aan.
  // Op de homepage doet initHero dat, maar die sectie bestaat hier niet.
  startOnthulling();

  initCoverDiepte(sectie);
  initCoverSlider(sectie);
  // Zelfde marquee-band als op de homepage; de tekst schuift met de scroll
  initLint(sectie);
}

function initMagazine(wortel = document) {
  initCover(wortel);
  // De lint-band is een eigen sectie geworden; init hem waar hij ook staat
  if (wortel.querySelector?.("[data-lint-tekst]")) initLint(wortel);
  initInhoud(wortel);
  initEdities(wortel);
  // De productkaart in de spread doet mee met de veer-hover
  initPilHover(wortel);
}

document.addEventListener("DOMContentLoaded", () => initMagazine());
document.addEventListener("shopify:section:load", (e) => initMagazine(e.target));
